import type { FC } from 'react'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { Bookmark, BookmarkCheck } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { apiFetch, errorMessage } from '@/lib/api-client'
import { useSite } from '@/components/generals/site-context'
import { Button } from '@/components/ui/button'

interface SaveJobButtonProps {
    jobId: string
    saved: boolean
    variant?: 'icon' | 'full'
    className?: string
    onChange?: (saved: boolean) => void
}

/** Bookmarks a job. Signed-out visitors are sent to sign in and brought back. */
export const SaveJobButton: FC<SaveJobButtonProps> = ({ jobId, saved, variant = 'icon', className, onChange }) => {
    const { viewer } = useSite()
    const router = useRouter()
    const [isSaved, setIsSaved] = useState(saved)
    const [pending, setPending] = useState(false)

    useEffect(() => setIsSaved(saved), [saved])

    const toggle = async () => {
        if (!viewer) {
            toast.info('Sign in to save jobs and come back to them later.')
            await router.push(`/login?next=${encodeURIComponent(router.asPath)}`)
            return
        }

        const next = !isSaved
        setIsSaved(next)
        setPending(true)

        try {
            await apiFetch('/api/saved', { method: next ? 'POST' : 'DELETE', body: { jobId } })
            onChange?.(next)
            toast.success(next ? 'Saved. Find it any time under Saved jobs.' : 'Removed from saved jobs.')
        }
        catch (error) {
            setIsSaved(!next)
            toast.error(errorMessage(error))
        }
        finally {
            setPending(false)
        }
    }

    const Icon = isSaved ? BookmarkCheck : Bookmark
    const label = isSaved ? 'Saved' : 'Save'

    if (variant === 'full') {
        return (
            <Button
                variant='outline'
                size='lg'
                onClick={() => void toggle()}
                disabled={pending}
                aria-pressed={isSaved}
                className={cn(isSaved && 'border-primary/30 bg-primary/8 text-primary hover:bg-primary/12', className)}
            >
                <Icon />
                {label}
            </Button>
        )
    }

    return (
        <Button
            variant='ghost'
            size='icon'
            onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                void toggle()
            }}
            disabled={pending}
            aria-pressed={isSaved}
            aria-label={isSaved ? 'Remove from saved jobs' : 'Save job'}
            className={cn('relative z-10 text-muted-foreground', isSaved && 'text-primary', className)}
        >
            <Icon className={cn('size-5', isSaved && 'fill-primary/15')} />
        </Button>
    )
}
